import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import type { ProjectPhase } from "@/lib/offer-types";
import { invalidateFor } from "@/lib/cache-registry";
import { recomputeProjectTotal, recomputeUpsellAmount } from "./use-budget-lines";
import { isProjectLockedError, toastProjectLocked } from "@/lib/db-errors";

export interface BudgetLineUpdate {
  description?: string;
  phase?: ProjectPhase;
  quantity?: number | null;
  unit?: string | null;
  quantity_hours?: number | null;
  amount?: number;
  cost_type?: "internal" | "external";
  estimated_cost?: number | null;
  template_id?: string | null;
}

export interface BudgetLineInsert extends Required<Pick<BudgetLineUpdate, "description" | "phase" | "amount" | "cost_type">> {
  project_id: string;
  quantity?: number | null;
  unit?: string | null;
  quantity_hours?: number | null;
  estimated_cost?: number | null;
  template_id?: string | null;
  /** VIB-2866: rekkefølge innen fase. Utelatt = sorteres etter linjer med verdi. */
  sort_order?: number | null;
}

interface UpdateVars {
  id: string;
  project_id: string;
  patch: BudgetLineUpdate;
}

interface DeleteVars {
  id: string;
  project_id: string;
}

/**
 * VIB-586: CRUD for budget_lines fra Tilbud-fanen i prosjektdetaljer.
 * Read-only for vanlige brukere; superadmin kan redigere via OkonomiTilbudPage.
 *
 * Alle mutations invaliderer "project-detail" så Tilbud + Timetildeling
 * deler samme cache.
 */
export function useUpdateBudgetLine() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({ id, project_id, patch }: UpdateVars) => {
      const { error } = await supabase.from("budget_lines").update(patch).eq("id", id);
      if (error) throw new Error(error.message);
      // Hvis linjen er en mersalg-line, sync cached amount_excl_vat.
      const { data: bl } = await supabase
        .from("budget_lines")
        .select("upsell_id")
        .eq("id", id)
        .maybeSingle();
      if (bl?.upsell_id) await recomputeUpsellAmount(bl.upsell_id);
      await recomputeProjectTotal(project_id);
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "budget_lines", { projectId: vars.project_id });
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Kunne ikke oppdatere linje: ${(err as Error).message}`);
    },
  });
}

export function useCreateBudgetLine() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: BudgetLineInsert) => {
      const { data, error } = await supabase
        .from("budget_lines")
        .insert(input)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      await recomputeProjectTotal(input.project_id);
      return data;
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "budget_lines", { projectId: vars.project_id });
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Kunne ikke opprette linje: ${(err as Error).message}`);
    },
  });
}

export function useDeleteBudgetLine() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({ id, project_id }: DeleteVars) => {
      // Hent upsell_id FØR sletting så vi kan re-summere mersalg etterpå.
      const { data: bl } = await supabase
        .from("budget_lines")
        .select("upsell_id")
        .eq("id", id)
        .maybeSingle();

      const { error } = await supabase.from("budget_lines").delete().eq("id", id);
      if (error) throw new Error(error.message);

      if (bl?.upsell_id) await recomputeUpsellAmount(bl.upsell_id);
      await recomputeProjectTotal(project_id);
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "budget_lines", { projectId: vars.project_id });
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Kunne ikke slette linje: ${(err as Error).message}`);
    },
  });
}

export interface OfferMetadataPatch {
  offer_total?: number;
  overhead_pct?: number;
  discount_pct?: number;
  discount_amount?: number;
  startup_invoice_pct?: number | null;
  final_invoice_pct?: number | null;
}

/**
 * Upsert project_offer_metadata + sync projects.total_amount når
 * offer_total endres. Brukes når superadmin redigerer overhead/rabatt
 * direkte på Tilbud-fanen (uten å re-importere PDF).
 */
export function useUpdateOfferMetadata() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({
      project_id,
      patch,
    }: {
      project_id: string;
      patch: OfferMetadataPatch;
    }) => {
      const { data: existing } = await supabase
        .from("project_offer_metadata")
        .select("*")
        .eq("project_id", project_id)
        .maybeSingle();

      const merged = {
        project_id,
        offer_total: patch.offer_total ?? existing?.offer_total ?? 0,
        overhead_pct: patch.overhead_pct ?? existing?.overhead_pct ?? 15,
        discount_pct: patch.discount_pct ?? existing?.discount_pct ?? 0,
        discount_amount: patch.discount_amount ?? existing?.discount_amount ?? 0,
        startup_invoice_pct:
          patch.startup_invoice_pct ?? existing?.startup_invoice_pct ?? 60,
        final_invoice_pct:
          patch.final_invoice_pct ?? existing?.final_invoice_pct ?? 40,
        include_travel_in_overhead:
          existing?.include_travel_in_overhead ?? false,
      };

      const { error } = await supabase
        .from("project_offer_metadata")
        .upsert(merged, { onConflict: "project_id" });
      if (error) throw new Error(error.message);

      // Sync total_amount fra offer_total + mersalg (mersalg får ikke overhead).
      await recomputeProjectTotal(project_id);
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "project_offer_metadata", { projectId: vars.project_id });
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Kunne ikke lagre tilbud-metadata: ${(err as Error).message}`);
    },
  });
}

/**
 * Splitt en intern oppgave i intern + ekstern: reduserer original
 * `quantity_hours` og `amount`, og oppretter en ny ekstern budget_line på
 * samme phase med restbeløpene. Eksisterende `budget_line_assignments` på
 * den interne delen røres ikke — de gjelder fortsatt det reduserte
 * timeantallet.
 *
 * Brukes når en del av en oppgave settes ut til ekstern leverandør
 * (f.eks. "Regi/foto prep 16t" → 8t Helge + 8t innleid).
 */
export interface SplitBudgetLineInput {
  /** Original linje som skal beholdes (intern del). */
  source_id: string;
  project_id: string;
  /** Nye verdier på intern (original) linje. */
  internal: { quantity_hours: number; amount: number };
  /** Verdier på ny ekstern linje. */
  external: {
    description: string;
    amount: number;
    estimated_cost: number;
  };
}

export function useSplitBudgetLineToExternal() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: SplitBudgetLineInput) => {
      const { data: source, error: srcErr } = await supabase
        .from("budget_lines")
        .select("phase, description, unit, upsell_id")
        .eq("id", input.source_id)
        .single();
      if (srcErr) throw new Error(srcErr.message);

      const { error: updErr } = await supabase
        .from("budget_lines")
        .update({
          quantity_hours: input.internal.quantity_hours,
          amount: input.internal.amount,
        })
        .eq("id", input.source_id);
      if (updErr) throw new Error(updErr.message);

      const { error: insErr } = await supabase.from("budget_lines").insert({
        project_id: input.project_id,
        phase: source.phase,
        description: input.external.description,
        cost_type: "external",
        unit: source.unit ?? "stk",
        quantity: 0,
        quantity_hours: 0,
        amount: input.external.amount,
        estimated_cost: input.external.estimated_cost,
        upsell_id: source.upsell_id ?? null,
      });
      if (insErr) throw new Error(insErr.message);

      if (source.upsell_id) await recomputeUpsellAmount(source.upsell_id);
      await recomputeProjectTotal(input.project_id);
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "budget_lines", { projectId: vars.project_id });
      toast.success("Oppgaven ble splittet til intern + ekstern");
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.project_id, navigate });
        return;
      }
      toast.error(`Kunne ikke splitte oppgaven: ${(err as Error).message}`);
    },
  });
}
